import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Text,
  Icon,
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@salestrak/ui";
import {
  dataToField,
  fieldDto,
  getDefaultValue,
} from "@salestrak/custom-fields";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { EntityTag } from "#app/components/entity";
import { CustomFieldRenderer } from "../custom-fields/custom-field-renderers";
import type { IssueTypeWithFields, CustomFieldDef } from "./issue-view-types";


interface MigrateIssueTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueTypes: IssueTypeWithFields[];
  /** The target type the user wants to migrate to */
  issueTypeId: string;
  /** The issue's current custom field values */
  values: Record<string, unknown>;
  issueName: string;
  onConfirm: (issueTypeId: string, values: Record<string, unknown>) => void;
}

export function MigrateIssueTypeDialog({
  open,
  onOpenChange,
  issueTypes,
  issueTypeId,
  values,
  issueName,
  onConfirm,
}: MigrateIssueTypeDialogProps) {
  const [newValues, setNewValues] = useState<Record<string, unknown>>({});

  const targetType = issueTypes.find((t) => t.issueTypeId === issueTypeId);

  const requiredFieldsById = useMemo(() => {
    const map: Record<string, CustomFieldDef> = {};
    for (const f of targetType?.fields ?? []) {
      if (f.required && f.field) {
        map[f.field.customFieldId] = f.field;
      }
    }
    return map;
  }, [targetType?.fields]);

  const requiredFieldsNeedingValues = useMemo(() => {
    return (targetType?.fields ?? [])
      .filter((f) => f.required && f.field)
      .map((f) => f.field!)
      .filter((field) => {
        const existing = values[field.customFieldId];
        if (existing == null || existing === "") return true;
        const defaultVal = getDefaultValue(field, requiredFieldsById);
        return JSON.stringify(existing) === JSON.stringify(defaultVal);
      });
  }, [targetType?.fields, values, requiredFieldsById]);

  const hasAllValues = requiredFieldsNeedingValues.every(
    (field) => newValues[field.customFieldId] != null,
  );

  const formValues = useMemo(() => {
    const result: Record<string, unknown> = {};
    for (const field of requiredFieldsNeedingValues) {
      const raw = newValues[field.customFieldId];
      if (raw != null) {
        result[field.customFieldId] = dataToField(field.type, raw).fieldValue;
      }
    }
    return result;
  }, [newValues, requiredFieldsNeedingValues]);

  const handleValueChange = useCallback(
    (customFieldId: string, value: unknown) => {
      const field = requiredFieldsById[customFieldId];
      if (!field) return;
      // fieldDto coerces rather than throws.
      setNewValues((prev) => ({
        ...prev,
        [customFieldId]: fieldDto(field.type, value),
      }));
    },
    [requiredFieldsById],
  );

  const handleMigrate = useCallback(() => {
    onConfirm(issueTypeId, newValues);
  }, [onConfirm, issueTypeId, newValues]);

  // Reset when dialog opens
  useEffect(() => {
    if (open) setNewValues({});
  }, [open]);

  const needsFields = requiredFieldsNeedingValues.length > 0;

  const fieldsTable = needsFields && targetType ? (
    <Box gap="md">
      <Box paddingBottom="lg">
        <Box
          direction="row"
          border="bottom"
          borderColor="border"
        >
          <Box paddingX="md" paddingY="sm" justify="center" width={220}>
            <Text size="xs" weight="semibold" variant="muted">
              Field
            </Text>
          </Box>
          <Box fill="flex" paddingX="md" paddingY="sm" justify="center">
            <Text size="xs" weight="semibold" variant="muted">
              Value
            </Text>
          </Box>
        </Box>
        {requiredFieldsNeedingValues.map((field) => {
          const fieldTypeMeta = getFieldTypeMeta(field.type);
          return (
            <Box
              key={field.customFieldId}
              direction="row"
              minHeight={40}
              border="bottom"
              borderColor="border"
            >
              {/* Label */}
              <Box
                direction="row"
                align="center"
                gap="sm"
                padding="sm"
                width={220}
              >
                <Icon name={fieldTypeMeta.icon} size="xs" color="subtle" />
                <Text size="sm" weight="medium" truncate={1}>
                  {field.name}
                  <Text variant="destructive"> *</Text>
                </Text>
              </Box>

              {/* Value */}
              <Box
                fill="flex"
                direction="row"
                align="stretch"
                hoverBackground
              >
                <Box fill="flex" minHeight={40}>
                  <CustomFieldRenderer
                    field={field}
                    value={formValues[field.customFieldId] ?? null}
                    onChangeValue={(v) =>
                      handleValueChange(field.customFieldId, v)
                    }
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  ) : null;

  const warning = (
    <Box gap="xs">
      <Text size="xs">
        Any empty custom fields will be automatically removed from{" "}
        {issueName}. This action cannot be undone.
      </Text>
      <Text size="xs" variant="muted">
        {requiredFieldsNeedingValues.length} required field
        {requiredFieldsNeedingValues.length !== 1 ? "s" : ""} need a value
      </Text>
    </Box>
  );

  const targetSummary = (
    <Box direction="row" align="center" gap="sm">
      <Text>Migrate to</Text>
      {targetType ? (
        <EntityTag
          entity={{
            kind: "issueType",
            id: targetType.issueTypeId,
            issueType: { name: targetType.name, icon: targetType.icon },
          }}
          weight="semibold"
          interactive={false}
        />
      ) : (
        <Text weight="semibold">Unknown</Text>
      )}
    </Box>
  );

  // One Dialog at every width — bottom-stuck on mobile, with the actions as
  // ordinary footer buttons.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle className="sr-only">
            Migrate to {targetType?.name ?? "Unknown"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Migrate this issue to a new type
          </DialogDescription>
          {targetSummary}
          {warning}
        </DialogHeader>
        <DialogPanel>
          <Box maxHeight={400} overflow="auto">
            {fieldsTable}
          </Box>
        </DialogPanel>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <Text>Cancel</Text>
          </Button>
          <Button
            variant="destructive-outline"
            onClick={handleMigrate}
            disabled={needsFields && !hasAllValues}
          >
            <Text>Migrate</Text>
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
